/**
 * src/shell/gallery/ArtworkEditModal.tsx
 *
 * Curator Artwork Creation & Edit Modal with Normalized Step-Up Challenge (RFC 9470).
 */

import React, { useState } from 'react';
import { ArtworkProduct, ArtworkImageRecord, ArtworkMetadata, PublicPrice } from '../../domain/inventory/types/product-types';
import { InventoryPolicy } from '../../domain/inventory/InventoryPolicy';
import { inventoryService } from '../../domain/inventory/InventoryService';
import { MultiAssetManager } from './components/MultiAssetManager';
import { LiveCardPreview } from './components/LiveCardPreview';

interface ArtworkEditModalProps {
  artwork: ArtworkProduct | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export const ArtworkEditModal: React.FC<ArtworkEditModalProps> = ({ artwork, isOpen, onClose, onSaved }) => {
  const isEditing = !!artwork;

  const [title, setTitle] = useState(artwork?.title || '');
  const [description, setDescription] = useState(artwork?.description || '');
  const [sku, setSku] = useState(artwork?.sku || `ART-${Date.now().toString().slice(-6)}`);
  const [artist, setArtist] = useState(artwork?.metadata.artist || '');
  const [medium, setMedium] = useState(artwork?.metadata.medium || 'Oil on Linen');
  const [yearCreated, setYearCreated] = useState<number>(artwork?.metadata.yearCreated || new Date().getFullYear());
  const [width, setWidth] = useState<number>(artwork?.metadata.dimensions.width || 80);
  const [height, setHeight] = useState<number>(artwork?.metadata.dimensions.height || 120);
  const [isPrivatePrice, setIsPrivatePrice] = useState(artwork?.price.isPrivate || false);
  const [priceAmount, setPriceAmount] = useState<number>(artwork?.price.isPrivate ? 10000 : (artwork?.price.amount || 5000));
  const [images, setImages] = useState<ArtworkImageRecord[]>(() => {
    if (artwork?.metadata.images && artwork.metadata.images.length > 0) {
      return artwork.metadata.images.map((img, idx) => ({
        url: img.url,
        caption: img.caption,
        isPrimary: typeof img.isPrimary === 'boolean' ? img.isPrimary : idx === 0,
        role: img.role || (idx === 0 ? 'primary' : 'detail'),
        fitMode: img.fitMode || 'contain',
      }));
    }
    return [
      {
        url: 'https://images.unsplash.com/photo-1579783900882-c0d3dad7b119?w=1200&auto=format&fit=crop&q=80',
        role: 'primary',
        isPrimary: true,
        fitMode: 'contain',
      },
    ];
  });
  const [statusMsg, setStatusMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  if (!isOpen) return null;

  const formattedPrice = isPrivatePrice
    ? 'Price on Request'
    : `USD ${Number(priceAmount || 0).toLocaleString()}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    setStatusMsg('');

    const validImages = images.filter((img) => img.url.trim().length > 0);
    if (validImages.length === 0) {
      setErrorMsg('At least one media asset URL is required');
      return;
    }

    try {
      const requiresStepUp = InventoryPolicy.requiresHighValueStepUp({
        amount: isPrivatePrice ? null : priceAmount,
        currency: 'USD',
      });

      if (requiresStepUp) {
        setStatusMsg('🔐 RFC 9470 Step-Up Challenge: Authorizing high-value artwork catalog change...');
      }

      const price = (isPrivatePrice
        ? {
            isPrivate: true,
            amount: null,
            currency: artwork?.price?.currency || 'USD',
            formatted: 'Price on Request',
          }
        : {
            isPrivate: false,
            amount: Number(priceAmount),
            currency: artwork?.price?.currency || 'USD',
            formatted: `${artwork?.price?.currency || 'USD'} ${Number(priceAmount).toLocaleString()}`,
          }) as PublicPrice;

      const metadata: ArtworkMetadata = {
        artist,
        medium,
        dimensions: { width: Number(width), height: Number(height), unit: 'cm' },
        yearCreated: Number(yearCreated),
        images: validImages,
        provenance: artwork?.metadata.provenance || [
          {
            id: `prov_${Date.now()}`,
            timestamp: new Date().toISOString(),
            event: 'creation',
            custodian: artist,
            notes: 'Registered in Sovereign Fine Art Mesh Ledger',
          },
        ],
        certificateOfAuthenticity: artwork?.metadata.certificateOfAuthenticity,
      };

      if (!artwork) {
        await inventoryService.createArtwork({
          tenantId: 'tenant_default',
          sku,
          title: title.trim(),
          description: description.trim() || undefined,
          price,
          metadata,
        });
      } else {
        await inventoryService.updateArtwork({
          ...artwork,
          sku,
          title: title.trim(),
          description: description.trim() || undefined,
          price,
          metadata: {
            ...artwork.metadata,
            ...metadata,
          },
        });
      }

      onSaved();
      onClose();
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to save artwork');
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="artwork-modal-heading"
      className="fixed inset-0 z-[1050] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
    >
      <div className="w-full max-w-2xl bg-bgCard border border-border rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <h3 id="artwork-modal-heading" className="m-0 text-base font-bold text-white flex items-center gap-2">
            <span>{isEditing ? 'Edit Artwork' : '➕ Register New Fine Art Piece'}</span>
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-400 border border-sky-500/30">TAILWIND-READY</span>
          </h3>
          <button onClick={onClose} className="bg-transparent border-none text-textMuted hover:text-white text-lg cursor-pointer transition-colors">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 flex flex-col gap-4 overflow-y-auto">
          {errorMsg && <div className="p-2.5 rounded-md bg-red-500/20 text-red-400 text-xs border border-red-500/30">{errorMsg}</div>}
          {statusMsg && <div className="p-2.5 rounded-md bg-sky-500/20 text-sky-400 text-xs border border-sky-500/30">{statusMsg}</div>}

          <div>
            <label className="text-xs font-semibold text-textMuted">Artwork Title</label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Untitled (Study in Blue)"
              className="w-full px-3 py-2 rounded-md bg-bgInput border border-border text-white text-sm mt-1 focus:outline-none focus:border-accent"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-textMuted flex items-center justify-between">
              <span>Description</span>
              <span className="text-[10px] text-textMuted/70 font-normal">Provenance & narrative</span>
            </label>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Artwork narrative, curation notes, or provenance..."
              className="w-full px-3 py-2 rounded-md bg-bgInput border border-border text-white text-sm mt-1 resize-y focus:outline-none focus:border-accent font-sans"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-textMuted">Artist</label>
              <input
                type="text"
                required
                value={artist}
                onChange={(e) => setArtist(e.target.value)}
                placeholder="e.g. Julian Vance"
                className="w-full px-3 py-2 rounded-md bg-bgInput border border-border text-white text-sm mt-1 focus:outline-none focus:border-accent"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-textMuted">SKU</label>
              <input
                type="text"
                required
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                className="w-full px-3 py-2 rounded-md bg-bgInput border border-border text-white text-sm mt-1 focus:outline-none focus:border-accent"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-semibold text-textMuted">Medium</label>
              <input
                type="text"
                value={medium}
                onChange={(e) => setMedium(e.target.value)}
                placeholder="e.g. Oil on Canvas"
                className="w-full px-3 py-2 rounded-md bg-bgInput border border-border text-white text-sm mt-1 focus:outline-none focus:border-accent"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-textMuted">Year</label>
              <input
                type="number"
                value={yearCreated}
                onChange={(e) => setYearCreated(Number(e.target.value))}
                className="w-full px-3 py-2 rounded-md bg-bgInput border border-border text-white text-sm mt-1 focus:outline-none focus:border-accent"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-textMuted">Width (cm)</label>
              <input
                type="number"
                value={width}
                onChange={(e) => setWidth(Number(e.target.value))}
                className="w-full px-3 py-2 rounded-md bg-bgInput border border-border text-white text-sm mt-1 focus:outline-none focus:border-accent"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-textMuted">Height (cm)</label>
              <input
                type="number"
                value={height}
                onChange={(e) => setHeight(Number(e.target.value))}
                className="w-full px-3 py-2 rounded-md bg-bgInput border border-border text-white text-sm mt-1 focus:outline-none focus:border-accent"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-textMuted">Pricing</label>
              <label className="text-[11px] text-accent flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={isPrivatePrice}
                  onChange={(e) => setIsPrivatePrice(e.target.checked)}
                  className="rounded border-border bg-bgInput"
                />
                Private (Price on Request)
              </label>
            </div>
            {!isPrivatePrice && (
              <input
                type="number"
                value={priceAmount}
                onChange={(e) => setPriceAmount(Number(e.target.value))}
                placeholder="Amount in USD"
                className="w-full px-3 py-2 rounded-md bg-bgInput border border-border text-white text-sm mt-1 focus:outline-none focus:border-accent"
              />
            )}
          </div>

          {/* Multi-Asset Manager */}
          <MultiAssetManager images={images} onChange={setImages} />

          {/* Live Card Preview Panel */}
          <LiveCardPreview
            title={title}
            artist={artist}
            medium={medium}
            yearCreated={yearCreated}
            description={description}
            priceFormatted={formattedPrice}
            images={images}
          />

          <div className="flex justify-end gap-2.5 pt-3 mt-1 border-t border-border shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md bg-transparent border border-slate-700 text-textMuted text-xs hover:text-white transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2 rounded-md bg-accent hover:bg-sky-400 text-slate-950 text-xs font-bold transition-colors cursor-pointer"
            >
              Save to Mesh
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
